/* BeadznThreadz cart helpers — shared add-to-cart for upsell cards, drawer upsell loader, gift wrap. */
(function () {
  if (window.BZCart) return;

  const cartEl = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  /**
   * Add one or more variants to cart and refresh the Dawn cart drawer / notification.
   * @param {Array<{id:number|string, quantity?:number, properties?:object}>} items
   */
  async function add(items, trigger) {
    const cart = cartEl();
    const body = {
      items: items.map((i) => ({ id: Number(i.id), quantity: i.quantity || 1, properties: i.properties || {} })),
    };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
      if (trigger && typeof cart.setActiveElement === 'function') cart.setActiveElement(trigger);
    }

    const res = await fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}.js`.replace('.js.js', '.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    const first = data.items ? data.items[0] : data;
    const state = Object.assign({}, data, { id: first && first.variant_id, key: first && first.key });

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bz-cart', productVariantId: first && first.variant_id, cartData: state });
    }

    if (cart && data.sections && typeof cart.renderContents === 'function') {
      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      cart.renderContents(state);
    } else if (!cart) {
      window.location = (window.routes && window.routes.cart_url) || '/cart';
    }
    return data;
  }

  /* Delegated click: [data-bz-add] buttons (optionally paired with a [data-bz-mini-variant] select) */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-bz-add]');
    if (!btn || btn.hasAttribute('data-bz-add-custom')) return;
    event.preventDefault();
    const wrap = btn.closest('[data-bz-mini-add]');
    const select = wrap && wrap.querySelector('[data-bz-mini-variant]');
    const id = select ? select.value : btn.dataset.bzAdd;
    if (!id) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');
    try {
      await add([{ id }], btn);
    } catch (e) {
      if (window.BZ) window.BZ.toast(e.message);
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-disabled');
    }
  });

  /* ---------- Drawer / cart upsell loader ---------- */
  const cache = new Map();
  class BzCartUpsell extends HTMLElement {
    connectedCallback() {
      const productId = this.dataset.productId;
      const url = this.dataset.url;
      if (!url) return;
      const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
      const key = url + '|' + productId;
      const render = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const src = doc.querySelector('[data-bz-upsell-source]');
        if (!src) return this.remove();
        src.querySelectorAll('[data-product-id]').forEach((card) => {
          if (exclude.includes(card.dataset.productId)) card.closest('li')?.remove();
        });
        const items = src.querySelectorAll('li');
        if (!items.length) return this.remove();
        const limit = parseInt(this.dataset.limit || '6', 10);
        items.forEach((li, i) => i >= limit && li.remove());
        const target = this.querySelector('[data-bz-upsell-target]');
        if (target) target.innerHTML = src.innerHTML;
        this.classList.add('is-loaded');
        if (window.BZ) window.BZ.init(this);
      };
      if (cache.has(key)) return render(cache.get(key));
      const fullUrl = productId ? `${url}&product_id=${productId}` : url;
      fetch(fullUrl)
        .then((r) => r.text())
        .then((html) => {
          cache.set(key, html);
          render(html);
        })
        .catch(() => this.remove());
    }
  }
  if (!customElements.get('bz-cart-upsell')) customElements.define('bz-cart-upsell', BzCartUpsell);

  /* ---------- Gift wrap / message (cart attributes) ---------- */
  let giftTimer;
  function saveAttributes(attrs) {
    return fetch(`${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`.replace('.js.js', '.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    });
  }
  document.addEventListener('change', (event) => {
    const toggle = event.target.closest('[data-bz-gift-toggle]');
    if (!toggle) return;
    const box = toggle.closest('[data-bz-gift]');
    const msgWrap = box && box.querySelector('[data-bz-gift-message-wrap]');
    if (msgWrap) msgWrap.hidden = !toggle.checked;
    const attrs = {};
    attrs[toggle.dataset.attr || 'Gift wrap'] = toggle.checked ? 'Yes' : '';
    if (!toggle.checked && box) {
      const msg = box.querySelector('[data-bz-gift-message]');
      if (msg) attrs[msg.dataset.attr || 'Gift message'] = '';
    }
    saveAttributes(attrs);
  });
  document.addEventListener('input', (event) => {
    const msg = event.target.closest('[data-bz-gift-message]');
    if (!msg) return;
    clearTimeout(giftTimer);
    giftTimer = setTimeout(() => {
      const attrs = {};
      attrs[msg.dataset.attr || 'Gift message'] = msg.value;
      saveAttributes(attrs);
    }, 500);
  });

  /* ---------- Cart page: refresh order summary after items are added elsewhere ---------- */
  function refreshSummary() {
    const footer = document.getElementById('main-cart-footer');
    if (!footer || !footer.dataset.id) return;
    fetch(`${(window.routes && window.routes.cart_url) || '/cart'}?section_id=${footer.dataset.id}`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const src = doc.querySelector('.js-contents');
        const dest = footer.querySelector('.js-contents');
        if (src && dest) dest.innerHTML = src.innerHTML;
        footer.classList.toggle('is-empty', false);
      })
      .catch(() => {});
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source !== 'cart-items') refreshSummary();
    });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source !== 'cart-items') refreshSummary();
        });
      }
    });
  }

  /* ---------- Cart page: save for later (moves the line to the wishlist) ---------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-bz-save-later]');
    if (!btn) return;
    event.preventDefault();
    const handle = btn.dataset.handle;
    if (window.BZ && handle && !window.BZ.wishlist.has(handle)) window.BZ.wishlist.toggle(handle);
    const cartItems = btn.closest('cart-items');
    if (cartItems && typeof cartItems.updateQuantity === 'function') {
      cartItems.updateQuantity(btn.dataset.index, 0, { currentTarget: btn });
    }
    const label = cartItems && cartItems.dataset.saveLabel;
    const wl = (window.BZ_CONFIG && window.BZ_CONFIG.wishlist) || {};
    if (window.BZ) window.BZ.toast(label || wl.added || 'Saved to your wishlist', wl.page || null, 'View wishlist');
  });

  /* ---------- Gift note character counter ---------- */
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-bz-note]');
    if (!note) return;
    const count = note.parentElement.querySelector('[data-bz-note-count]');
    if (count) count.textContent = `${note.value.length}/${note.maxLength > 0 ? note.maxLength : 200}`;
  });

  /* ---------- "Wear it together": recommendations for the first cart item ---------- */
  class BzCartTogether extends HTMLElement {
    connectedCallback() {
      if (this.dataset.performed === 'true' || !this.dataset.productId || !this.dataset.url) return;
      this.load(this.dataset.intent || 'related');
    }
    load(intent) {
      const url = `${this.dataset.url}&product_id=${this.dataset.productId}&intent=${intent}`;
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const grid = doc.querySelector('[data-bz-together-grid]');
          const hasProducts = grid && grid.querySelector('[data-bz-together-product]');
          if (!hasProducts) {
            if (intent === 'complementary') this.load('related');
            return;
          }
          const dest = this.querySelector('[data-bz-together-grid]');
          if (dest) dest.innerHTML = grid.innerHTML;
          if (window.BZ) window.BZ.init(this);
        })
        .catch(() => {});
    }
  }
  if (!customElements.get('bz-cart-together')) customElements.define('bz-cart-together', BzCartTogether);

  window.BZCart = { add, saveAttributes, refreshSummary };
})();
